/**
 * MasterDataDunnageFormDialog — Create/Edit `dunnage`.
 * 1:1 port of Access `frmMdQryDunnageAdd`. Compact Sales Contract style.
 *
 * Single-column (Access all inputs Left=3012), field order by Top:
 *   Dunnage Code → Dunnage → Note.
 * `dunnage_code` là PK auto-suggest (Access `Suggest_IndexName("dunnage")` gọi ở
 * btnNew của frmMdMainModItemsValuation) — áp /access-suggest:
 *   - New: auto-suggest khi mở + blur-check trùng → tự đổi mã + alert vàng.
 *   - Edit: disable, không suggest/check.
 * VBA errCheck: code trống → "Please enter valid Dunnage Code!"; dunnage trống →
 *   "Please type Dunnage!"; code trùng (create) → "Dunnage Code existed! Please
 *   enter a new Code!" (BE raise).
 */
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdDunnageMutation,
  useLazyCheckMdDunnageCodeQuery,
  useLazySuggestMdDunnageCodeQuery,
  useUpdateMdDunnageMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataDunnage } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import {
  compactDialogContentSx,
  dialogHeaderBtnSx,
  dialogTitleActionsSx,
  dialogTitleSx,
  dialogTitleTextSx,
  roSx,
} from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataDunnage | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  dunnage_code: string
  dunnage: string
  note: string
}

const EMPTY: FormState = { dunnage_code: '', dunnage: '', note: '' }

export default function MasterDataDunnageFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createDunnage, { isLoading: creating }] = useCreateMdDunnageMutation()
  const [updateDunnage, { isLoading: updating }] = useUpdateMdDunnageMutation()
  const [triggerSuggest, suggestState] = useLazySuggestMdDunnageCodeQuery()
  const [triggerCheck] = useLazyCheckMdDunnageCodeQuery()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)
  const [regenNotice, setRegenNotice] = useState<{ from: string; to: string } | null>(null)
  const [precheckBusy, setPrecheckBusy] = useState(false)
  const precheckBusyRef = useRef(false)

  // Hydrate — chỉ depend [open, mode, initialValue] (access-suggest anti #4).
  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    setRegenNotice(null)
    setPrecheckBusy(false)
    precheckBusyRef.current = false
    if (isEdit && initialValue) {
      setForm({
        dunnage_code: initialValue.dunnage_code ?? '',
        dunnage: initialValue.dunnage ?? '',
        note: initialValue.note ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  // New: auto-suggest dunnage_code khi mở (lazy, guard chỉ set khi trống).
  useEffect(() => {
    if (!open || isEdit) return
    let cancelled = false
    void triggerSuggest(undefined, false).unwrap().then((res) => {
      if (cancelled) return
      const newVal = (res.dunnage_code || '').trim()
      if (!newVal) return
      setForm((prev) => (prev.dunnage_code.trim() ? prev : { ...prev, dunnage_code: newVal }))
    }).catch(() => {})
    return () => { cancelled = true }
  }, [open, isEdit, triggerSuggest])

  // Dismiss alert khi user sửa code khác mã suggest.
  useEffect(() => {
    if (regenNotice && form.dunnage_code !== regenNotice.to) setRegenNotice(null)
  }, [form.dunnage_code, regenNotice])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.dunnage_code.trim()
  const dunnageEmpty = !form.dunnage.trim()
  const canSave = !codeEmpty && !dunnageEmpty && !loading

  // Blur field PK → check trùng, nếu có thì suggest mã mới (access-suggest).
  const handleCodeBlur = async () => {
    if (isEdit) return
    const val = form.dunnage_code.trim()
    if (!val) return
    precheckBusyRef.current = true
    setPrecheckBusy(true)
    try {
      const check = await triggerCheck(val, false).unwrap()
      if (!check.exists) return
      const next = await triggerSuggest(undefined, false).unwrap()
      const newVal = (next.dunnage_code || '').trim()
      if (newVal && newVal !== val) {
        setForm((prev) => ({ ...prev, dunnage_code: newVal }))
        setRegenNotice({ from: val, to: newVal })
      }
    } catch {
      /* BE raise lúc Save nếu thật sự trùng — không poison UX */
    } finally {
      precheckBusyRef.current = false
      setPrecheckBusy(false)
    }
  }

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    if (precheckBusyRef.current) return // race guard (blur + click Save cùng tick)
    if (codeEmpty || dunnageEmpty) {
      setShowErrors(true)
      return
    }
    const body = {
      dunnage: form.dunnage.trim(),
      note: form.note.trim() || null,
    }
    try {
      if (isEdit && initialValue) {
        await updateDunnage({ code: initialValue.dunnage_code, body }).unwrap()
        snack.success('Dunnage updated')
      } else {
        await createDunnage({ dunnage_code: form.dunnage_code.trim(), ...body }).unwrap()
        snack.success('Dunnage created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      // access-suggest anti #13: duplicate race → auto re-suggest + alert vàng.
      const msg = getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create')
      const lower = msg.toLowerCase()
      if (!isEdit && (lower.includes('exist') || lower.includes('duplicate'))) {
        try {
          const next = await triggerSuggest(undefined, false).unwrap()
          const newVal = (next.dunnage_code || '').trim()
          const oldVal = form.dunnage_code.trim()
          if (newVal && newVal !== oldVal) {
            setForm((prev) => ({ ...prev, dunnage_code: newVal }))
            setRegenNotice({ from: oldVal, to: newVal })
            return
          }
        } catch {
          /* fallthrough → snack error */
        }
      }
      snack.error(msg)
    }
  }

  const codeHelper = isEdit
    ? 'Dunnage Code cannot be changed after creation.'
    : precheckBusy
      ? 'Verifying code…'
      : suggestState.isFetching
        ? 'Suggesting next code…'
        : 'Suggested code below; edit if needed.'

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Dunnage' : 'New Dunnage'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.dunnage_code}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        {regenNotice ? (
          <Alert severity="warning" sx={{ mb: 1.25, py: 0.25 }}>
            Dunnage Code <b>{regenNotice.from}</b> was just created — suggested new code{' '}
            <b>{regenNotice.to}</b>.
          </Alert>
        ) : null}
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Dunnage Code"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.dunnage_code}
              onChange={(e) => set('dunnage_code', e.target.value)}
              onBlur={() => void handleCodeBlur()}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={codeHelper}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Dunnage"
              size="small"
              fullWidth
              required
              error={showErrors && dunnageEmpty}
              value={form.dunnage}
              onChange={(e) => set('dunnage', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Note"
              size="small"
              fullWidth
              multiline
              minRows={3}
              value={form.note}
              onChange={(e) => set('note', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
